import {ApolloClient} from "apollo-client";
import {ApolloLink} from "apollo-link";
import {RetryLink} from "apollo-link-retry";
import {InMemoryCache, IntrospectionFragmentMatcher} from "apollo-cache-inmemory";
import {onError} from "apollo-link-error";
import {setContext} from "apollo-link-context";
import {createUploadLink} from "apollo-upload-client";
import {GRAPHQL_URL} from "./constants";
import fragmentTypes from "./fragmentTypes";
import uniqBy from "lodash/uniqBy";

if (!GRAPHQL_URL) {
  console.error("Transitlog graphql URL not set!");
}

function createErrorLink(UIStore) {
  function notifyError(type, message, target) {
    if (UIStore) {
      return UIStore.addError(type, message, target);
    }

    console.warn(`${type} error: ${message}, target: ${target}`);
  }

  return onError(({graphQLErrors, networkError, operation}) => {
    if (graphQLErrors) {
      uniqBy(graphQLErrors, (err) => err.message).map((err) => console.error(err));
    }

    if (networkError) {
      notifyError(
        "Network",
        "Network error. Please refresh the page to try again, or contact the Reittiloki team if the error persists.",
        operation.operationName
      );
    }
  });
}

let createdClient = null;

export const getClient = (UIStore) => {
  if (createdClient) {
    return createdClient;
  }

  const toApollo2Introspection = (possibleTypesMap) => ({
    __schema: {
      types: Object.entries(possibleTypesMap).map(([name, possibleTypes]) => ({
        kind: "UNION",
        name,
        possibleTypes: possibleTypes.map((t) => ({name: t})),
      })),
    },
  });

  const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData: fragmentTypes.possibleTypes
      ? toApollo2Introspection(fragmentTypes.possibleTypes)
      : fragmentTypes,
  });

  const errorLink = createErrorLink(UIStore);

  const retryLink = new RetryLink({
    delay: (count) => ({
      initial: 2000 * count,
      max: 4000,
      jitter: false,
    }),

    attempts: (count) => count <= 2,
  });

  const contextLink = setContext((operation, prevContext) => {
    const {headers} = prevContext || {};

    if (typeof operation.variables._cache !== "undefined") {
      const useCache = !!operation.variables._cache;
      delete operation.variables._cache;

      return {
        ...prevContext,
        headers: {
          ...headers,
          "x-skip-cache": !useCache ? "true" : "false",
        },
      };
    }
    return prevContext;
  });

  const cache = new InMemoryCache({
    fragmentMatcher,
    addTypename: true,
  });

  const uploadLink = createUploadLink({
    uri: GRAPHQL_URL,
    credentials: "include",
  });

  createdClient = new ApolloClient({
    link: ApolloLink.from([errorLink, retryLink, contextLink, uploadLink]),
    cache: cache,
  });

  return createdClient;
};
